import { useState, useRef } from 'react';
import { Box, IconButton, Popover, Typography, TextField, InputAdornment, Tabs, Tab, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const EMOJI_LIST = [
  '🔥', '🛡️', '📊', '🔒', '✅', '⚠️', '🚀', '🎯',
  '📈', '📉', '💡', '🔍', '🧪', '🏷️', '📋', '🔧',
  '⚡', '🌐', '🔗', '📝', '🤖', '📦', '🎨', '🧩',
  '🔔', '📌', '💬', '📢', '🏠', '👁️', '🔄', '📊',
  '🛒', '💰', '📱', '🖥️', '🔐', '🗂️', '📂', '🗑️',
  '✨', '❌', '🟢', '🟡', '🔴', '🟠', '🔵', '⚫',
];

interface EmojiPickerProps {
  value: string | null;
  onChange: (emoji: string | null) => void;
  checkPackId?: number;
  onUpload?: (file: File) => Promise<void>;
  onDeleteCustom?: () => Promise<void>;
}

function isCustomEmoji(val: string | null): boolean {
  return !!val && val.startsWith('/uploads/');
}

export default function EmojiPicker({ value, onChange, checkPackId, onUpload, onDeleteCustom }: EmojiPickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    setUploading(true);
    try {
      await onUpload(file);
      setAnchor(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const renderPreview = () => {
    if (!value) return '➕';
    if (isCustomEmoji(value)) {
      return <Box component="img" src={value} alt="emoji" sx={{ width: 28, height: 28, objectFit: 'contain' }} />;
    }
    return value;
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ width: 48, height: 48, fontSize: 24, border: '1px solid #ccc', borderRadius: 1 }}
      >
        {renderPreview()}
      </IconButton>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 300 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 36 }}>
            <Tab label="Emojis" sx={{ minHeight: 36, py: 0 }} />
            {checkPackId != null && <Tab label="Custom" sx={{ minHeight: 36, py: 0 }} />}
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Suche…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px',
                  },
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                {value && (
                  <IconButton
                    size="small"
                    onClick={() => { onChange(null); setAnchor(null); }}
                    title="Entfernen"
                    sx={{ fontSize: 18, width: 36, height: 36, color: 'error.main' }}
                  >
                    ✕
                  </IconButton>
                )}
                {EMOJI_LIST.filter((e) => !search || e.includes(search)).map((emoji) => (
                  <IconButton
                    key={emoji}
                    size="small"
                    onClick={() => { onChange(emoji); setAnchor(null); }}
                    sx={{ fontSize: 20, width: 36, height: 36, bgcolor: emoji === value ? 'action.selected' : 'transparent' }}
                  >
                    {emoji}
                  </IconButton>
                ))}
              </Box>
            </Box>
          )}

          {tab === 1 && checkPackId != null && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                PNG, JPG oder SVG hochladen (max. 256 KB). Wird auf 32×32 px skaliert.
              </Typography>

              {isCustomEmoji(value) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="img" src={value!} alt="custom" sx={{ width: 32, height: 32, border: '1px solid #eee', borderRadius: 0.5 }} />
                  <Typography variant="body2">Aktuelles Custom-Emoji</Typography>
                  {onDeleteCustom && (
                    <IconButton size="small" color="error" onClick={async () => { await onDeleteCustom(); setAnchor(null); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={handleFileChange} />
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                size="small"
              >
                {uploading ? 'Wird hochgeladen…' : 'Bild hochladen'}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
